import React, { Component } from 'react';
import { AgGridReact } from '@ag-grid-community/react';
import { AllModules } from '@ag-grid-enterprise/all-modules';
// The app's own alert/confirm component (lmsobzones, actualSobConfigPanel, ... all use it), so the
// Run SOB preconditions read like the rest of the app instead of the browser's "<host> says" box.
import SweetAlert from 'react-bootstrap-sweetalert';
import { renderIgnoredTrucksNotice } from './truckNotices';

var redirectURL = require('../redirectURL');
var moment = require('moment');

// ---------------------------------------------------------------------------------------------
// One-click SOB (client, 2026-10-08; audit layout 2026-10-14). Opened from the Load Summary row
// action and from the plan view, it replaces Truck Availability -> Assign Transporters ->
// Load Share -> Run SOB. Top to bottom:
//   - SOB status of the plan (its own field - a failed SOB never blocks the plan view)
//   - SOB AUDIT LOG: one row per run (lm_sob_run_log) with a view icon opening the vehicle
//     availability AS AT THAT RUN (lm_sob_run_trucks) in Summary / Trucks tabs, the same two
//     views the Truck Availability screen has
//   - trip-wise mapping: what each truck trip ended up with (read from the plan output, so it
//     also covers plans SOB'd before vehicle assignment or run logging existed)
//   - available now: today's live trucks, shown only while a re-run is still possible
//   - Run SOB (incremental: allocated trips stay, only waiting trips are filled from trucks no
//     plan of this warehouse has used today), then polls until done
// The slider only slides in once its data is loaded; until then the app's standard loader runs,
// and a refresh shows the same loader rather than an inline "Loading..." line.
// Props: transactionId, loadingPortCode, dataType, open, onClose(), onDone()
// ---------------------------------------------------------------------------------------------

var POLL_MS = 5000;

// Manual truck counts are judged against the INDIAN calendar day - the same one Node
// (getLmTrucksDetails) and the engine (dbcalls.db_get_manual_available_vehicles) use - never the
// browser's. A user sitting in another zone must still be shown what the run will actually do.
var IST_OFFSET = "+05:30";

function istToday() {
    return moment().utcOffset(IST_OFFSET).format("YYYY-MM-DD");
}

// "14-Oct" - a date the user can match against what they typed on the Truck Availability screen,
// without a year that adds nothing to a count entered days ago at most.
function fmtDay(d) {
    var m = moment(d, "YYYY-MM-DD", true);
    return m.isValid() ? m.format("DD-MMM") : String(d || "");
}

var STATUS_STYLE = {
    "COMPLETED":   { label: "Completed",   bg: "#2e7d32" },
    "PARTIAL":     { label: "Partially allocated", bg: "#f9a825" },
    "IN_PROGRESS": { label: "In progress", bg: "#1976d2" },
    "FAILED":      { label: "Failed",      bg: "#c62828" },
    "NOT_RUN":     { label: "Not run",     bg: "#607d8b" }
};

var GRID_DEFAULTS = { sortable: true, filter: true, floatingFilter: true, resizable: true };

// Plot height of the per-zone columns, in px.
var PLOT_H = 110;

// The SOB modes, each with its OWN approved share and actual share.
//   - Regular / Dedicated / Courier are the three planning types (data_type 1 / 3 / 2)
//   - Hub is NOT a fourth planning type: hub trips ride inside a REGULAR plan and are allocated
//     against a share of their own (is_hub 1), which is why it carries data_type 1 + hub.
//   - Air Courier is a COURIER sub-mode (is_air_courier 1) and exists only on a plan run with VIP
//     planning flagged; it is never shown otherwise.
// `trucks` says whether a LIVE truck feed exists for the mode. Only Regular and Hub have one -
// dedicated / courier / air vehicles are fabricated by the engine from the SOB rows, so that half
// of the table stays blank for them rather than showing numbers that do not exist.
var MODES = [
    { key: "REGULAR",   label: "Regular",     data_type: 1, hub: false, air: false, trucks: true },
    { key: "HUB",       label: "Hub",         data_type: 1, hub: true,  air: false, trucks: true },
    { key: "DEDICATED", label: "Dedicated",   data_type: 3, hub: false, air: false, trucks: false },
    { key: "COURIER",   label: "Courier",     data_type: 2, hub: false, air: false, trucks: false },
    { key: "AIR",       label: "Air Courier", data_type: 2, hub: false, air: true,  trucks: false }
];

function modeDef(key) {
    for (var i = 0; i < MODES.length; i++) {
        if (MODES[i].key === key) { return MODES[i]; }
    }
    return MODES[0];
}

// Which mode a trip of the plan belongs to. Read from the plan's own output row, so it is right
// for a single-stage plan and for an automated Regular -> Dedicated -> Courier cascade alike, and
// it works for plans SOB'd before the run log existed.
function modeOfTrip(t) {
    var vt = String(t.vehicle_type || "").toUpperCase();
    if (vt.indexOf("AIR") >= 0) { return "AIR"; }
    if (t.hub_trip) { return "HUB"; }
    var pt = String(t.planning_type || "").toUpperCase();
    if (pt.indexOf("DEDICATED") >= 0) { return "DEDICATED"; }
    if (pt.indexOf("COURIER") >= 0) { return "COURIER"; }
    return "REGULAR";
}

// compact = smaller badge for dense grids (Load Summary).
export function sobStatusBadge(status, compact) {
    var st = STATUS_STYLE[status] || STATUS_STYLE["NOT_RUN"];
    var size = compact ? 'padding:0 8px;font-size:10.5px;line-height:18px;border-radius:9px;'
                       : 'padding:0 10px;font-size:12px;border-radius:10px;';
    return '<span style="background-color:' + st.bg + ';color:#fff;' + size +
        'display:inline-block;white-space:nowrap;">' + st.label + '</span>';
}

function vehicleKey(v) {
    return String(v || "").toUpperCase().replace(/[^0-9A-Z]/g, "");
}

function zoneKey(z) {
    return String(z || "").toUpperCase().replace(/[^0-9A-Z]/g, "");
}

// A vehicle type reduced to its SIZE, so a hub trip and the truck that would serve it compare
// equal: a trip reads '32 FT HUB Vehicle' while the portal feed reports the same physical truck as
// '32 FT Vehicle' and earmarks it by zone (the split sob_lsp_hub makes). Comparing the raw strings
// would say a hub trip needs a type no truck ever reports.
function normalisedVehicleType(v) {
    return String(v || "").toUpperCase().replace(/\bHUB\b/g, "").replace(/[^0-9A-Z]/g, "");
}

function fmtDate(v) {
    if (!v) { return ""; }
    var m = moment(v);
    return m.isValid() ? m.format("DD-MM-YYYY HH:mm") : String(v);
}

// Compact stamp for the audit rows - the year adds nothing next to a run that happened today.
function fmtRunTime(v) {
    if (!v) { return ""; }
    var m = moment(v);
    return m.isValid() ? m.format("DD-MMM HH:mm") : String(v);
}

// Which modes a run covered and what each allocated, from the run log's own per-stage record
// (`stage_summary`). A run made before that was recorded shows "-" rather than a guess.
var STAGE_LABEL = { REGULAR: "Regular", HUB: "Hub", DEDICATED: "Dedicated", COURIER: "Courier",
                    AIR: "Air Courier", "AIR COURIER": "Air Courier" };

function stageText(run, full) {
    var stages = (run && run.stage_summary) || [];
    if (stages.length === 0) { return "-"; }
    return stages.map(function (s) {
        var key = String(s.planning_type || "").toUpperCase();
        var label = STAGE_LABEL[key] || (s.planning_type || "?");
        // "with a transporter / total" for the stage - a courier stage allocated by PLANNING shows
        // its trips as kept, not as newly allocated, so counting only allocated_now would read 0.
        var done = (s.new_trips || 0) + (s.kept_trips || 0);
        var line = label + " " + done + "/" + (s.eligible_trips || done);
        if (full) {
            line += " trip(s) with a transporter (" + (s.new_trips || 0) + " this run, "
                 +  (s.kept_trips || 0) + " kept, " + (s.pending_trips || 0) + " waiting), "
                 +  (s.trucks_seen || 0) + " truck(s) seen";
            if (s.availability_source) { line += ", " + sourceText(s.availability_source); }
        }
        return line;
    }).join(full ? "\n" : " · ");
}

// The run-level source is a join of every stage's source, and a stage that saw no trucks used to
// record the literal "None" - so a run reads "None, Portal", which looks like a source called
// "None" and says nothing about WHICH stage had no feed. The engine no longer writes it that way,
// but runs already recorded still carry it, so it is cleaned here too: drop the no-feed markers
// as long as a real feed was read, and name the no-feed case plainly when that is all there was.
var NON_FEED_SOURCES = ["NONE", "NO FEED", "ALLOCATED DURING PLANNING"];

function sourceText(value) {
    var parts = String(value || "").split(",").map(function (s) { return s.trim(); })
        .filter(function (s) { return s !== ""; });
    if (parts.length === 0) { return "-"; }
    var real = parts.filter(function (s) { return NON_FEED_SOURCES.indexOf(s.toUpperCase()) < 0; });
    if (real.length > 0) { return real.join(", "); }
    return parts.map(function (s) { return s.toUpperCase() === "NONE" ? "No feed" : s; }).join(", ");
}

// "2m 14s" - how long the run took, for the audit row.
function duration(from, to) {
    if (!from || !to) { return ""; }
    var a = moment(from);
    var b = moment(to);
    if (!a.isValid() || !b.isValid()) { return ""; }
    var secs = Math.max(b.diff(a, "seconds"), 0);
    if (secs < 60) { return secs + "s"; }
    return Math.floor(secs / 60) + "m " + (secs % 60) + "s";
}

export default class SobRunPanel extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            // the slider waits for the first load, so it never slides in empty
            ready: false,
            status: null,
            runs: [],
            // share + trucks of the ACTIVE mode (the tab) - see loadMode / applyMode
            trucks: [],
            approved: [],
            actualLoad: [],
            zoneTotals: [],
            activeMode: "",
            modeData: {},        // mode key -> its fetched share + trucks, so a tab switch is free
            modeLoading: false,
            hubNames: {},        // zoneKey(hub name) -> true, the hub/regular truck split
            // Manual vehicle availability document of the WAREHOUSE (one per warehouse, per mode
            // inside it). Fetched once, lazily, by the Run preflight - null = not read yet.
            manualAvail: null,
            starting: false,
            // The preflight is async (it has to pull the modes the user never opened), so
            // `starting` alone leaves a window between the click and the prompt in which a second
            // click would fire a second preflight. The button is disabled on BOTH.
            checking: false,
            message: "",
            // Run preconditions (client, 2026-10-16). A BLOCKER uses `alert` (OK only, run refused);
            // a WARNING uses `confirm` (Cancel / Continue). Both checks are evaluated on the same
            // click and reported in ONE prompt, so the user never dismisses two boxes in a row.
            alert: { open: false, title: "", message: "", type: "warning" },
            confirm: { open: false, title: "", message: "" },
            // run detail popup
            detailRun: null,
            detailLog: null,
            detailTrucks: [],
            detailLoading: false,
            detailTab: "summary"
        };
        this._timer = null;
        this.trucksGrid = React.createRef();
    }

    componentDidMount() {
        if (this.props.open) { this.load(); }
    }

    componentDidUpdate(prev) {
        if (this.props.open && (!prev.open || prev.transactionId !== this.props.transactionId)) {
            this.setState({ ready: false, detailRun: null });
            this.load();
        }
        if (!this.props.open && prev.open) {
            this.stopPolling();
        }
    }

    componentWillUnmount() {
        this.stopPolling();
    }

    stopPolling() {
        if (this._timer) { clearTimeout(this._timer); this._timer = null; }
    }

    async load() {
        this.stopPolling();
        // Refresh re-reads the run preconditions too (manualAvail back to null = not read yet),
        // so a count entered on the Truck Availability screen while this panel was open is seen.
        this.setState({ loading: true, message: "", modeData: {}, manualAvail: null,
                        alert: { open: false, title: "", message: "", type: "warning" },
                        confirm: { open: false, title: "", message: "" } });
        var runsP = redirectURL.post("/lmconsignees/getSobRunLogs", { transaction_id: this.props.transactionId })
            .then((r) => { this.setState({ runs: this.ownRuns((r.data && r.data.records) || []) }); })
            .catch(() => { this.setState({ runs: [] }); });
        // The tab list is derived from the plan's own trips, so the status has to land first.
        var st = await this.fetchStatus();
        var modes = this.planModes(st);
        var hubP = modes.filter(function (m) { return m.hub; }).length > 0
            ? this.loadHubNames() : Promise.resolve();
        await Promise.all([runsP, hubP]);
        await this.loadMode(this.defaultMode(modes, st), true);
        this.setState({ loading: false, ready: true });
        if (this.state.status && this.state.status.sob_status === "IN_PROGRESS") {
            this.schedulePoll();
        }
    }

    // ---- modes (Regular / Hub / Dedicated / Courier / Air Courier) ----

    // The modes THIS plan's SOB actually covers, with each one's trip counts. Built from the plan
    // output rather than from a fixed list, so a plain Regular plan shows one mode and an automated
    // cascade shows every stage it ran - and neither claims a mode it never planned.
    planModes(status) {
        var st = status || this.state.status || {};
        var vip = !!st.vip_planning;
        var counts = {};
        (st.trips || []).forEach(function (t) {
            var k = modeOfTrip(t);
            if (!counts[k]) { counts[k] = { trips: 0, allocated: 0, waiting: 0 }; }
            counts[k].trips += 1;
            if (t.transporter) { counts[k].allocated += 1; } else { counts[k].waiting += 1; }
        });
        // Air courier is NOT allocated by SOB at all - the engine excludes air rows from every
        // stage and strips air transporters out of the SOB masters, because air courier is planned
        // on the VIP path. So its tab is offered only on a VIP plan that actually produced air
        // rows, it carries no allocated count (there is none to carry), and the table below it is
        // the air share of business for reference.
        var air = parseInt(st.air_trips || 0) || 0;
        return MODES.filter(function (m) {
            if (m.air) { return vip && air > 0; }
            return !!counts[m.key];
        }).map(function (m) {
            if (m.air) {
                return Object.assign({}, m, { trips: air, allocated: null, waiting: 0 });
            }
            return Object.assign({}, m, counts[m.key]);
        });
    }

    // Open on the plan's own planning type (the mode the Run button acts on first); fall back to
    // whichever mode has trips still waiting, then to the first one.
    defaultMode(modes, status) {
        var dt = parseInt(this.props.dataType || (status && status.data_type)) || 1;
        // No trips recorded (SOB never run, or a plan with no truck trips) - still open on the
        // plan's own planning type, never on a mode it was not planned with.
        var plain = MODES.filter(function (m) { return m.data_type === dt && !m.hub && !m.air; });
        if (modes.length === 0) { return (plain[0] || MODES[0]).key; }
        var own = modes.filter(function (m) { return m.data_type === dt && !m.hub && !m.air; });
        if (own.length > 0) { return own[0].key; }
        var waiting = modes.filter(function (m) { return m.waiting > 0; });
        return (waiting.length > 0 ? waiting[0] : modes[0]).key;
    }

    activeModeDef() {
        return modeDef(this.state.activeMode || "REGULAR");
    }

    // Hub names of the warehouse. A hub truck is identified by its ZONE being a hub name - the live
    // feed reports it by SIZE ('32 FT Vehicle'), never '32 FT HUB Vehicle' - which is exactly how
    // the engine splits them (sob_lsp_hub.split_hub_trucks).
    loadHubNames() {
        return redirectURL.post("/masters/getHubMasterData",
                                { loading_port_code: this.props.loadingPortCode, is_active: 1 })
            .then((r) => {
                var names = {};
                ((r.data && r.data.hubData) || []).forEach(function (h) {
                    var k = zoneKey(h.hub_name);
                    if (k) { names[k] = true; }
                });
                this.setState({ hubNames: names });
            })
            .catch(() => { this.setState({ hubNames: {} }); });
    }

    // Share (+ trucks where a feed exists) of one mode. Cached per mode, so switching tabs costs
    // nothing; Refresh re-reads the active one.
    loadMode(key, force) {
        var cached = this.state.modeData[key];
        if (cached && !force) {
            this.applyMode(key, cached);
            return Promise.resolve(cached);
        }
        this.setState({ activeMode: key, modeLoading: true });
        return this.fetchMode(key).then((data) => { this.applyMode(key, data); return data; });
    }

    // The raw fetch of ONE mode, WITHOUT touching the active tab. Split out of loadMode so the Run
    // preflight can pull the modes the user never opened (loadMode is lazy - only the tab in view
    // is cached) without the panel visibly hopping from tab to tab while it checks them.
    fetchMode(key) {
        var mode = modeDef(key);
        var lpc = this.props.loadingPortCode;
        var today = moment.parseZone().format("YYYY-MM-DD");
        var approvedP = mode.hub
            ? redirectURL.post("/lmconsignees/getLmHubShareOfBusiness",
                               { loading_port_code: lpc, data_type: mode.data_type, is_hub: 1,
                                 filter_start_date: today })
            : redirectURL.post("/lmconsignees/getLmShareOfBusiness",
                               Object.assign({ loading_port_code: lpc, data_type: mode.data_type,
                                               filter_start_date: today },
                                             mode.air ? { is_air_courier: 1 } : {}));
        // Actual share, so the panel shows the same approved / actual / deficit picture SOB ranks on.
        // The courier endpoint reads the collection NAMED IN THE BODY, and surface courier and air
        // courier keep their load share in different collections - sending the wrong name (or none)
        // reads another mode's numbers, or throws.
        var actualUrl = mode.hub ? "/lmconsignees/getActualHubSobPercentage"
                                 : (mode.data_type === 2 ? "/lmconsignees/getActualCourierSobPercentage"
                                                         : "/lmconsignees/getActualSobPercentage");
        var actualP = redirectURL.post(actualUrl,
                                       Object.assign({ loading_port_code: lpc, data_type: mode.data_type },
                                                     mode.air ? { is_air_courier: true, air_courier_transporter: 1 }
                                                              : {},
                                                     mode.data_type === 2
                                                        ? { is_hub: 0,
                                                            collection: mode.air ? "lmaircourierloadshare"
                                                                                 : "lmcourierloadshare" }
                                                        : {}));
        // ⚠ `is_hub` is NOT optional. Regular and Hub are BOTH data_type 1 and are told apart by
        // this flag alone, and Node resolves which mode's MANUAL counts to fold into the response
        // from (data_type + is_hub) - with no flag it resolves to REGULAR, so the Hub tab would
        // read Regular's manual counts and a Hub manual entry would do nothing at all. The LIVE
        // feed half is unaffected: that is still split hub/regular by ZONE in trucksForMode.
        var trucksP = mode.trucks
            ? redirectURL.post("/lmconsignees/getLmTrucksDetails",
                               { loading_port_code: lpc, data_type: mode.data_type,
                                 is_hub: mode.hub ? 1 : 0, with_ignored: 1 })
            : Promise.resolve({ data: [] });
        return Promise.all([
            approvedP.catch(() => ({ data: {} })),
            actualP.catch(() => ({ data: {} })),
            trucksP.catch(() => ({ data: [] }))
        ]).then((res) => {
            var a = res[0].data || {};
            var b = res[1].data || {};
            // with_ignored -> { trucks, ignored }; an older Node returns the plain array.
            var t = res[2].data;
            var data = {
                approved: a.sobData || [],
                sobCount: parseInt(a.sobCount || 0) || 0,
                actualLoad: b.loadShareData || [],
                zoneTotals: b.zoneTotals || [],
                trucks: Array.isArray(t) ? t : ((t && Array.isArray(t.trucks)) ? t.trucks : []),
                ignoredTrucks: (t && !Array.isArray(t) && Array.isArray(t.ignored)) ? t.ignored : []
            };
            // Functional setState: the preflight fetches several modes IN PARALLEL, and a
            // read-modify-write of this.state.modeData would drop every sibling that landed in the
            // same batch. The data is also RETURNED, so a caller never has to wait for the flush.
            this.setState(function (prev) {
                var cache = Object.assign({}, prev.modeData);
                cache[key] = data;
                return { modeData: cache };
            });
            return data;
        });
    }

    applyMode(key, data) {
        // The approved share is NOT lifted into state any more: the Run preconditions are judged
        // per mode out of modeData (see runSob), so the tab in view no longer decides anything.
        this.setState({
            activeMode: key, modeLoading: false,
            approved: data.approved, actualLoad: data.actualLoad,
            zoneTotals: data.zoneTotals, trucks: data.trucks,
            ignoredTrucks: data.ignoredTrucks || []
        });
    }

    // Keep only this plan's runs and show the newest first. Node filters by transaction_id, but an
    // older build ignores that and returns every warehouse's runs - which would show another plan's
    // run numbers in this panel - so the filter is applied here as well.
    ownRuns(records) {
        var txn = String(this.props.transactionId || "");
        return (records || [])
            .filter(function (r) { return !r.transaction_id || String(r.transaction_id) === txn; })
            .sort(function (a, b) { return (parseInt(b.run_no) || 0) - (parseInt(a.run_no) || 0); });
    }

    fetchStatus() {
        return redirectURL.post("/lmconsignees/getSobRunStatus", { transactionId: this.props.transactionId })
            .then((r) => {
                if (r.data && r.data.status === "success") {
                    this.setState({ status: r.data });
                } else {
                    this.setState({ message: (r.data && r.data.message) || "Could not read SOB status" });
                }
                return r.data;
            })
            .catch(() => { this.setState({ message: "Could not read SOB status" }); });
    }

    // Polling must not flash the standard loader every 5 seconds - it refreshes in place and only
    // re-reads the audit log once the run is over, when a new row exists to show.
    schedulePoll() {
        this.stopPolling();
        this._timer = setTimeout(async () => {
            var data = await this.fetchStatus();
            if (data && data.sob_status === "IN_PROGRESS") {
                this.schedulePoll();
            } else if (data) {
                redirectURL.post("/lmconsignees/getSobRunLogs", { transaction_id: this.props.transactionId })
                    .then((r) => { this.setState({ runs: this.ownRuns((r.data && r.data.records) || []) }); })
                    .catch(() => {});
                if (this.props.onDone) { this.props.onDone(data); }
            }
        }, POLL_MS);
    }

    // ---- Run preconditions (client, 2026-10-16) ----
    // Two things must exist before SOB can allocate anything: an APPROVED SHARE to rank
    // transporters by, and VEHICLES to hand out. Both are judged ACROSS EVERY MODE THIS RUN
    // COVERS, not just the tab in view - the old gate read the approved share of the plan's own
    // mode alone, so an automated Regular -> Dedicated -> Courier cascade could start with two of
    // its three stages unable to allocate a single trip, and nothing said so.
    //   NOTHING configured in ANY mode  -> BLOCKER, the run does not start.
    //   SOME modes configured           -> WARNING naming the rest, Cancel / Continue.
    // The two checks are independent (a plan can have share but no trucks, or the reverse) and are
    // evaluated on the same click; if both are only warnings they are reported in ONE prompt.
    //
    // ⚠ EACH CHECK IS SCOPED TO THE MODES IT CAN ACTUALLY SPEAK FOR - a warning about something
    // that changes nothing is worse than no warning, because it sends the user to configure a
    // screen that will not alter the plan:
    //   - AIR COURIER IS OUT OF BOTH CHECKS. SOB does not allocate air at all: the engine strips
    //     air transporters out of the SOB masters and air is planned on the VIP path, so neither
    //     an air approved share nor air vehicles can change what a run does.
    //   - FEEDLESS MODES (Dedicated / Courier, `trucks: false`) are judged on vehicles ONLY WHEN
    //     their manual tick is on, because that tick is exactly what decides whether the engine
    //     reads counts or fabricates its own supply. See the note at the check itself. They are
    //     always in the APPROVED SOB check, which is the data they are ranked from either way.
    //   - MANUAL COUNTS EXPIRE (server, 2026-10-16), so a count dated any day but today counts as
    //     ABSENT here, exactly as planning treats it. A mode can therefore be TICKED but STALE: it
    //     is still judged (the user asked for manual supply) and still comes out missing, but it
    //     is reported with its own wording and its date - "dated 14-Oct, not today" is a thing the
    //     user can go and fix, where a bare "no vehicle availability" sends them hunting for
    //     something that is already sitting on the screen. See manualAvailableOn.

    // Manual vehicle availability of the warehouse, fetched once.
    // ⚠ The route uses the REQUEST BODY AS ITS MONGO FILTER, so nothing may be added to it - a
    // `mode` key would filter the warehouse's own document out and read as "no manual availability
    // anywhere". The per-mode counts live INSIDE the document instead.
    loadManualAvailability(force) {
        if (this.state.manualAvail !== null && !force) { return Promise.resolve(this.state.manualAvail); }
        var self = this;
        return redirectURL.post("/lmconsignees/getmanualvehicleavailability",
                                { plant_code: this.props.loadingPortCode })
            .then(function (r) {
                // One document per warehouse, but the route answers with the matching DOCUMENTS,
                // so an array arrives (the Truck Availability screen reads data[0] the same way).
                var d = r && r.data;
                var doc = Array.isArray(d) ? (d.length > 0 ? d[0] : {}) : (d || {});
                self.setState({ manualAvail: doc });
                return doc;
            })
            .catch(function () { self.setState({ manualAvail: {} }); return {}; });
    }

    // Does this mode have a manual count to allocate from?
    // RESOLUTION, mirroring the server exactly: if `modes[KEY]` is present use IT; otherwise fall
    // back to the LEGACY warehouse-wide pair (use_manual_vehicle_availability + availabilitydata)
    // - but ONLY for a mode with a live feed, see manualModeConfig. The fallback is per MODE, not
    // per document: a document that gained `modes.HUB` but has no `modes.REGULAR` must still read
    // Regular off the legacy pair, or a warehouse that configured one mode would look as if it had
    // un-configured the others.
    manualModeConfig(doc, key) {
        if (!doc) { return {}; }
        var modes = doc.modes;
        if (modes && typeof modes === "object" && modes[key]) { return modes[key]; }
        // ⚠⚠ THE LEGACY PAIR IS INHERITED ONLY BY THE MODES THAT ALWAYS READ IT - Regular and Hub.
        // A FEEDLESS MODE (Dedicated / Courier) MUST NEVER inherit it: 5 of the 7 field warehouses
        // already carry `use_manual_vehicle_availability: true` with warehouse-wide counts that
        // were only ever read by the stages WITH a live feed - dedicated / courier consulted
        // availability at all for the first time with this change. Inheriting it would impose a
        // warehouse's REGULAR counts as the Dedicated / Courier truck pool and start judging those
        // modes on every one of those warehouses THE MOMENT THIS DEPLOYS, for configuration nobody
        // touched. The engine is strict in exactly the same way
        // (sob_utils.manual_availability_for_mode(..., require_explicit_mode=True) returns
        // (False, []) for a legacy document), so the two sides agree: only an explicit
        // `modes.DEDICATED` / `modes.COURIER` entry switches those modes on.
        if (!modeDef(key).trucks) { return {}; }
        return { use: doc.use_manual_vehicle_availability, availabilitydata: doc.availabilitydata };
    }

    // Is manual availability SWITCHED ON for this mode? Read separately from the counts because
    // the tick alone decides WHETHER a feedless mode is judged at all (see evaluatePreconditions):
    // on = the engine allocates from these counts, off = it fabricates its own supply. The flag is
    // read under both spellings - `use` inside a per-mode entry, the legacy field name at the top
    // of the document - so neither shape can read as "off" merely because of its key.
    manualTickOn(doc, key) {
        var cfg = this.manualModeConfig(doc, key);
        var use = (cfg.use !== undefined) ? cfg.use : cfg.use_manual_vehicle_availability;
        return use === true || use === 1;
    }

    // The DAY a mode's manual counts are for. Manual counts EXPIRE (Node getLmTrucksDetails and
    // dbcalls.db_get_manual_available_vehicles both ignore counts dated any day but today), so this
    // panel must resolve the date by the SAME chain or it would call a mode stocked that planning
    // has already dropped: explicit modes.<MODE>.available_on -> modes.<MODE>.updated_at -> the
    // document's created_date (a LEGACY document has no date of its own; its save date stands in).
    // The stored instants arrive as ISO strings whose UTC fields carry the IST WALL CLOCK, so
    // parseZone keeps that +00:00 and reads the IST calendar date back as-is; moment() would
    // convert it again and slide a late-evening save onto the next day.
    manualEffectiveDate(doc, key) {
        if (!doc) { return ""; }
        var own = (doc.modes && typeof doc.modes === "object" && doc.modes[key]) ? doc.modes[key] : {};
        if (own.available_on) { return String(own.available_on).trim(); }
        if (own.updated_at) { return moment.parseZone(own.updated_at).format("YYYY-MM-DD"); }
        if (doc.created_date) { return moment.parseZone(doc.created_date).format("YYYY-MM-DD"); }
        return "";
    }

    // TICKED but dated a day other than today: the user asked for manual supply, planning is not
    // using it. A date that cannot be read is never "stale" - same test as the server
    // (`availableOn && availableOn !== today`), so an unreadable value cannot fail a run.
    manualStale(doc, key) {
        if (!this.manualTickOn(doc, key)) { return false; }
        var d = this.manualEffectiveDate(doc, key);
        return !!d && d !== moment.parseZone().utcOffset("+05:30").format("YYYY-MM-DD");
    }

    manualHasVehicles(doc, key) {
        if (!doc) { return false; }
        // Counts that exist but are switched off are not available - the engine does not read them
        // in that case.
        if (!this.manualTickOn(doc, key)) { return false; }
        // Counts dated another day are not available either - planning ignores them (see
        // manualEffectiveDate). Without this the preflight would pass a run on last week's trucks.
        if (this.manualStale(doc, key)) { return false; }
        var rows = this.manualModeConfig(doc, key).availabilitydata;
        if (!Array.isArray(rows)) { return false; }
        for (var i = 0; i < rows.length; i++) {
            var row = rows[i] || {};
            var cells = Object.keys(row);
            for (var j = 0; j < cells.length; j++) {
                // A count cell is keyed "<zone>||<vehicle type>". transporter_name / zone and any
                // other bookkeeping key on the row is NOT a count and must never be read as one -
                // a transporter name is truthy and would make every row look stocked.
                if (cells[j].indexOf("||") < 0) { continue; }
                // A ZONE NOT SET count is never used - SOB serves a trip only from a truck earmarked
                // for its zone, in every mode (engine zone_available_for). Counting it here let the
                // preflight pass a mode whose only counts were zone-less, and SOB then allocated nothing.
                var cellZone = zoneKey(cells[j].split("||")[0]);
                if (cellZone === "" || cellZone === zoneKey("ZONE NOT SET")) { continue; }
                if ((parseFloat(row[cells[j]]) || 0) > 0) { return true; }
            }
        }
        return false;
    }

    // EITHER source counts: the live truck feed (Regular / Hub only - trucksForMode returns nothing
    // for a feedless mode) or a manual count entered for that mode. For a feedless mode this is
    // reached only when its manual tick is ON, which is precisely when the engine allocates from
    // those counts - so an empty pool there really does mean nothing can be allocated.
    modeHasVehicles(mode, data, manual) {
        if (this.trucksForMode(mode, (data || {}).trucks).length > 0) { return true; }
        return this.manualHasVehicles(manual, mode.key);
    }

    runSob() {
        var st = this.state.status;
        if (!st || !st.can_run) { return; }
        // Double-click guard, covering the async preflight as well as the POST itself.
        if (this.state.starting || this.state.checking) { return; }
        var modes = this.planModes(st);
        if (modes.length === 0) {
            // No trips recorded (SOB never run on a plan with no truck trips yet): the run still
            // acts on the plan's own planning type, so judge THAT mode rather than judging nothing
            // and letting an unconfigured warehouse through.
            modes = [modeDef(this.defaultMode(modes, st))];
        }
        this.setState({ checking: true, message: "" });
        var self = this;
        // Every mode of the run has to be LOADED before it can be judged - loadMode is lazy and
        // caches only the tab in view. Fetched in parallel, and from fetchMode's return value
        // rather than from state, which may not have flushed yet.
        var pending = modes.map(function (m) {
            var cached = self.state.modeData[m.key];
            return cached ? Promise.resolve(cached) : self.fetchMode(m.key);
        });
        Promise.all([this.loadManualAvailability()].concat(pending)).then(function (res) {
            var byMode = {};
            modes.forEach(function (m, i) { byMode[m.key] = res[i + 1] || {}; });
            self.setState({ checking: false });
            self.evaluatePreconditions(modes, byMode, res[0]);
        }).catch(function () {
            // A precondition that cannot be READ is not a violation: refusing a legal run because
            // a master fetch failed would strand the user with no way forward. The server-side
            // backstop on generateLoadBySOB still refuses the genuinely-unconfigured case.
            self.setState({ checking: false });
            self.startSob();
        });
    }

    evaluatePreconditions(modes, byMode, manual) {
        var self = this;
        var approvedMissing = [];
        var approvedJudged = [];
        var vehicleMissing = [];
        var vehicleJudged = [];
        modes.forEach(function (m) {
            var data = byMode[m.key] || {};
            // AIR COURIER IS OUT OF BOTH CHECKS: SOB never allocates it (the engine strips air
            // transporters out of the SOB masters; air is planned on the VIP path), so neither an
            // air approved share nor air vehicles can change what this run does. Judging it would
            // block or warn on configuration that is inert.
            if (m.air) { return; }
            approvedJudged.push(m.label);
            if ((parseInt(data.sobCount || 0) || 0) <= 0) { approvedMissing.push(m.label); }
            // A FEEDLESS MODE (Dedicated / Courier) IS JUDGED ON VEHICLES ONLY WHEN ITS MANUAL TICK
            // IS ON. The engine now asks `manual_supply_for_synthetic_stage(depot, mode)` FIRST
            // (sob_utils.get_available_dedicated_vehicle_details / ..._courier_...): with the tick
            // on, those counts ARE the truck supply - 3 manual trucks means the stage offers 3 -
            // so an empty pool means nothing gets allocated and the user must hear about it. With
            // the tick OFF the stage fabricates its own supply exactly as before (one truck per
            // requirement per transporter), so there is nothing the user could be missing and
            // judging it would send them to a screen that cannot change their plan.
            if (!m.trucks && !self.manualTickOn(manual, m.key)) { return; }
            vehicleJudged.push(m.label);
            if (!self.modeHasVehicles(m, data, manual)) {
                // Missing BECAUSE the counts are out of date is a different message from missing
                // because there are none: "dated 14-Oct, not today" points at a one-click fix
                // (re-date and save), where a bare "no vehicle availability" sends the user
                // hunting for configuration that is in fact sitting right there.
                if (self.manualStale(manual, m.key)) {
                    var d = self.manualEffectiveDate(manual, m.key);
                    vehicleMissing.push(m.label + " (manual counts are dated " +
                                        moment(d, "YYYY-MM-DD").format("DD-MMM") + ", not today)");
                } else {
                    vehicleMissing.push(m.label);
                }
            }
        });

        // BLOCKER = nothing at all, i.e. not one JUDGED mode has it. A partial configuration is a
        // warning instead: the modes that ARE configured allocate normally, and refusing the whole
        // run would punish them for a mode the user may not care about today. The `judged.length`
        // guards matter - a Dedicated-only plan has nothing to judge on vehicles, which is "no
        // check to make", NOT "everything is missing".
        var approvedBlocked = approvedJudged.length > 0 && approvedMissing.length === approvedJudged.length;
        var vehicleBlocked = vehicleJudged.length > 0 && vehicleMissing.length === vehicleJudged.length;

        if (approvedBlocked || vehicleBlocked) {
            var lines = [];
            // Each sentence names the modes IT judged, never "this plan" - after the scoping above
            // the two checks can cover different sets of modes, so one list for both would name a
            // mode the sentence does not apply to.
            if (approvedBlocked) {
                lines.push("No Approved SOB percentage is defined for today for " +
                           (approvedJudged.length > 1 ? "any mode of this plan (" + approvedMissing.join(", ") + ")"
                                                      : approvedJudged[0]) +
                           ". Set it on the Approved SOB screen.");
            }
            if (vehicleBlocked) {
                // vehicleMissing[0], not vehicleJudged[0]: blocked means the two lists cover the
                // same modes, and only the missing list carries the "dated ..., not today" note.
                lines.push("No vehicle availability for " +
                           (vehicleJudged.length > 1 ? "any mode of this plan (" + vehicleMissing.join(", ") + ")"
                                                     : vehicleMissing[0]) +
                           " - neither a live truck feed nor a manual count. Set it on the Truck " +
                           "Availability screen.");
            }
            lines.push("SOB has nothing to allocate until this is configured, so the run was not started.");
            var msg = lines.join("\n\n");
            this.setState({
                message: lines.slice(0, lines.length - 1).join(" "),
                alert: { open: true, type: "warning",
                         title: (approvedBlocked && vehicleBlocked) ? "SOB is not configured yet"
                              : (approvedBlocked ? "Approved SOB not configured"
                                                 : "Vehicle availability not configured"),
                         message: msg }
            });
            return;
        }

        var warn = [];
        if (approvedMissing.length > 0) {
            warn.push("No Approved SOB percentage for today: " + approvedMissing.join(", ") + ".");
        }
        if (vehicleMissing.length > 0) {
            warn.push("No vehicle availability (no live feed and no manual count): " +
                      vehicleMissing.join(", ") + ".");
        }
        if (warn.length === 0) { this.startSob(); return; }
        warn.push("Trips of " + (warn.length > 1 ? "those modes" : "that mode") +
                  " will stay waiting for a transporter; the rest will be allocated as usual.");
        warn.push("Continue with the run?");
        this.setState({
            confirm: { open: true, title: "Some modes cannot be allocated",
                       message: warn.join("\n\n") }
        });
    }

    // The POST itself. Reached only once the preconditions above are satisfied or accepted.
    startSob() {
        this.setState({ starting: true, message: "" });
        redirectURL.post("/lmconsignees/generateLoadBySOB", {
            transaction_id: this.props.transactionId,
            loading_port_code: this.props.loadingPortCode,
            data_type: this.props.dataType,
            // identity for the User Activities entry Node writes on every SOB request
            email: localStorage.getItem("email") || "",
            user_name: localStorage.getItem("username") || "",
            staff_id: localStorage.getItem("staff_id") || "",
            comp_code: localStorage.getItem("comp_code") || "",
            user_department: localStorage.getItem("lm_department") || "",
            user_role: localStorage.getItem("roles") || "",
            designation: localStorage.getItem("designation") || ""
        }).then((r) => {
            this.setState({ starting: false });
            if (r.data && r.data.status === "success") {
                var cur = Object.assign({}, this.state.status, {
                    sob_status: "IN_PROGRESS", sob_remarks: "SOB queued", can_run: false
                });
                this.setState({ status: cur });
                this.schedulePoll();
            } else {
                this.showStartFailure(r.data, "SOB could not be started");
            }
        }).catch((e) => {
            this.setState({ starting: false });
            // The same precondition rule, enforced SERVER-SIDE where a stale browser tab cannot
            // bypass it: a 400 carries `blocked` + its own message. axios rejects on 400, so the
            // body arrives on e.response.data (the app's standard shape for an error body).
            var body = (e && e.response && e.response.data) ? e.response.data : null;
            this.showStartFailure(body, "SOB could not be started: " + e.message);
        });
    }

    // One place to surface a refusal, whether it came back as a rejected 400 or as a 200 carrying
    // status 400 - the message is the server's own, not a generic failure, and a `blocked` marker
    // raises it to a dialog because it is a precondition the user has to go and fix.
    showStartFailure(body, fallback) {
        var msg = (body && body.message) ? body.message : fallback;
        var blocked = (body && body.blocked) ? String(body.blocked) : "";
        var patch = { starting: false, message: msg };
        if (blocked) {
            patch.alert = {
                open: true, type: "warning",
                title: blocked === "approved_sob" ? "Approved SOB not configured"
                     : (blocked === "vehicle_availability" ? "Vehicle availability not configured"
                                                           : "SOB is not configured yet"),
                message: msg + (body.modes && body.modes.length > 0
                                ? "\n\nModes affected: " + body.modes.join(", ") : "")
            };
        }
        this.setState(patch);
    }

    // ---- run detail (vehicle availability as at that run) ----
    openRunDetail(run) {
        if (!run) { return; }
        this.setState({ detailRun: run, detailLog: null, detailTrucks: [], detailLoading: true, detailTab: "summary" });
        redirectURL.post("/lmconsignees/getSobRunDetail", {
            transaction_id: run.transaction_id, run_no: run.run_no
        }).then((r) => {
            if (r.data && r.data.status === "success") {
                this.setState({ detailLog: r.data.log || {}, detailTrucks: r.data.trucks || [], detailLoading: false });
            } else {
                this.setState({ detailLoading: false });
            }
        }).catch(() => { this.setState({ detailLoading: false }); });
    }

    // Summary tab = the Trucks tab grouped, so the two can never disagree.
    detailSummaryRows() {
        var cells = {};
        this.state.detailTrucks.forEach(function (t) {
            var zone = String(t.zone || "").trim();
            var key = (t.planning_type || "") + "|" + zone + "|" + (t.transporter_name || "") + "|" + (t.vehicle_type || "");
            var c = cells[key];
            if (!c) {
                c = cells[key] = {
                    planning_type: t.planning_type || "", zone: zone || "(no zone)",
                    transporter_name: t.transporter_name || "", transporter_code: t.transporter_code || "",
                    vehicle_type: t.vehicle_type || "", available: 0, allocated: 0, used_earlier: 0,
                    no_zone: 0, free: 0
                };
            }
            c.available += 1;
            if (t.status === "Allocated") { c.allocated += 1; }
            else if (t.status === "Free") { c.free += 1; }
            else if (String(t.status || "").indexOf("No zone") === 0) { c.no_zone += 1; }
            else { c.used_earlier += 1; }
        });
        return Object.keys(cells).map(function (k) { return cells[k]; }).sort(function (a, b) {
            return (a.zone + a.transporter_name + a.vehicle_type).localeCompare(b.zone + b.transporter_name + b.vehicle_type);
        });
    }

    // Trucks of the ACTIVE mode. The feed does NOT label a hub truck '32 FT HUB Vehicle' - it
    // reports the plain size - so hub and regular trucks are separated by the truck's ZONE being a
    // HUB NAME, the same split the engine makes (sob_lsp_hub.split_hub_trucks). Dedicated / courier
    // / air modes have no feed at all and return nothing here.
    modeTrucks() {
        return this.trucksForMode(this.activeModeDef(), this.state.trucks);
    }

    // The hub/regular split of ONE mode's feed, on a truck list handed in. Parameterised so the
    // Run preflight can judge a mode that is not the tab in view, with ONE definition of the split
    // rather than a second copy that could drift from it.
    trucksForMode(mode, trucks) {
        if (!mode || !mode.trucks) { return []; }
        var hubs = this.state.hubNames || {};
        var wantHub = !!mode.hub;
        return (trucks || []).filter(function (t) {
            return (hubs[zoneKey(t.zone)] === true) === wantHub;
        });
    }

    // transporter x zone x vehicle type -> available / used today / free, from TODAY'S LIVE feed.
    // Distinct from the run detail above, which is the snapshot the run itself saw.
    // Vehicle types the ACTIVE mode's trips actually call for, normalised so a hub trip's
    // '32 FT HUB Vehicle' matches the feed's '32 FT Vehicle' - the portal reports a hub truck by
    // SIZE and earmarks it by zone, never by a HUB type name (same rule as sob_lsp_hub).
    planVehicleTypesForMode(modeKey) {
        var st = this.state.status || {};
        var types = {};
        (st.trips || []).forEach(function (t) {
            if (modeOfTrip(t) !== modeKey) { return; }
            types[normalisedVehicleType(t.vehicle_type)] = true;
        });
        return types;
    }

    availabilityRows() {
        var st = this.state.status || {};
        var used = st.used_today || [];
        var usedNumbers = {};
        var usedManual = {};
        // Manual counts are a separate pool per MODE (engine used_vehicles_today stage_label): a
        // Courier / Dedicated manual truck must not read as "used" on the Regular or Hub tab. The
        // allocation's planning_type is the same key as the mode (REGULAR / HUB / DEDICATED / COURIER).
        var activeKey = this.state.activeMode;
        used.forEach(function (a) {
            if (a.vehicle_source === "Manual") {
                if (a.planning_type && activeKey && String(a.planning_type) !== String(activeKey)) { return; }
                var k = String(a.transporter_code || "") + "|" + zoneKey(a.truck_zone) + "|" + String(a.truck_type || "");
                usedManual[k] = (usedManual[k] || 0) + 1;
            } else if (a.vehicle_number) {
                usedNumbers[vehicleKey(a.vehicle_number)] = true;
            }
        });
        var cells = {};
        this.modeTrucks().forEach(function (t) {
            var zone = String(t.zone || "").trim();
            var key = (t.transporter_name || "") + "|" + zone + "|" + (t.vehicle_type || "");
            var c = cells[key];
            if (!c) {
                c = cells[key] = { transporter: t.transporter_name || "", code: t.transporter_code || "", zone: zone,
                                   vehicle_type: t.vehicle_type || "", available: 0, used: 0, manual: 0 };
            }
            c.available += 1;
            if (t.source === "Manual") {
                c.manual += 1;
            } else if (usedNumbers[vehicleKey(t.vehicle_number)]) {
                c.used += 1;
            }
        });
        var rows = Object.keys(cells).map(function (k) {
            var c = cells[k];
            if (c.manual > 0) {
                var mk = c.code + "|" + zoneKey(c.zone) + "|" + c.vehicle_type;
                c.used += Math.min(c.manual, usedManual[mk] || 0);
            }
            c.free = Math.max(c.available - c.used, 0);
            return c;
        });

        // A truck of a size THIS PLAN never asked for is not "availability" - it is noise, and
        // read as a shortfall it is actively misleading. DEV GUR 20261016095512-30273a434ce8 had
        // one 14 FT truck punched on a day whose plan formed no 14 FT trip at all, and the panel
        // counted it among the trucks available to the run. SOB can only ever allocate a truck to
        // a trip of the same vehicle type, so a type with no trip is unusable by construction.
        // They are dropped from the table and reported as a count instead, so the number is not
        // hidden - just not presented as supply for this plan.
        var planTypes = this.planVehicleTypesForMode(this.state.activeMode);
        var hiddenTrucks = 0, hiddenTypes = {};
        var usable = [];
        rows.forEach(function (c) {
            if (planTypes[normalisedVehicleType(c.vehicle_type)]) { usable.push(c); return; }
            hiddenTrucks += c.available;
            hiddenTypes[c.vehicle_type] = true;
        });
        // Only applied when the plan told us which types it needs. A mode with no trips recorded
        // (SOB never run) would otherwise hide every truck and read as "no availability at all".
        if (Object.keys(planTypes).length > 0) {
            this._availHiddenTrucks = hiddenTrucks;
            this._availHiddenTypes = Object.keys(hiddenTypes).sort();
            rows = usable;
        } else {
            this._availHiddenTrucks = 0;
            this._availHiddenTypes = [];
        }
        rows.sort(function (a, b) {
            return (a.zone + a.transporter + a.vehicle_type).localeCompare(b.zone + b.transporter + b.vehicle_type);
        });
        return rows;
    }

    // Transaction id and SOB status live in the title bar; the counts they used to repeat are in
    // the tiles and the audit log below, so nothing is said twice.
    renderHeader() {
        var st = this.state.status || {};
        var pill = {
            background: "rgba(255,255,255,0.16)", borderRadius: "11px", padding: "1px 10px",
            fontSize: "11.5px", fontWeight: 600, whiteSpace: "nowrap"
        };
        return (
            <div className="slider-header" style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <span>SOB - Transporter &amp; Vehicle Assignment</span>
                <span style={pill}>{this.props.transactionId}</span>
                {this.state.ready ?
                    <span style={Object.assign({}, pill, { padding: "1px 6px" })}>
                        <span dangerouslySetInnerHTML={{ __html: sobStatusBadge(st.sob_status, true) }} />
                        {st.sob_run_no ? <span style={{ marginLeft: "6px" }}>Run {st.sob_run_no}</span> : ""}
                        {st.sob_finished_at ? <span style={{ marginLeft: "6px", fontWeight: 400 }}>{fmtDate(st.sob_finished_at)}</span> : ""}
                        {st.sob_status === "IN_PROGRESS" ? <span style={{ marginLeft: "6px", fontWeight: 400 }}>refreshing every 5s</span> : ""}
                    </span> : ""}
                <span onClick={this.props.onClose} style={{ cursor: "pointer", marginLeft: "auto" }}>✕</span>
            </div>
        );
    }

    // Mode tabs. Each mode has its own approved share, its own actual share and (Regular / Hub) its
    // own trucks, so the table below belongs to exactly one of them - showing them blended was the
    // gap this closes. Only the modes this plan actually planned are offered.
    renderModeTabs() {
        var modes = this.planModes();
        if (modes.length === 0) { return null; }
        var active = this.state.activeMode;
        var self = this;
        return (
            <div className="d-flex align-items-center" style={{ gap: "6px", flexWrap: "wrap", margin: "6px 0 2px 0" }}>
                {modes.map(function (m) {
                    var on = m.key === active;
                    return (
                        <button type="button" key={m.key}
                                onClick={function () { self.loadMode(m.key); }}
                                title={m.allocated === null
                                        ? m.trips + " air courier trip(s) - planned on the VIP path, not allocated by SOB"
                                        : (m.trips + " truck trip(s) in this plan, " + m.allocated + " with a transporter"
                                           + (m.trucks ? "" : " - no live vehicle feed for " + m.label))}
                                style={{ border: "1px solid " + (on ? "#1565c0" : "#cfd8e3"),
                                         background: on ? "#1565c0" : "#fff", color: on ? "#fff" : "#334e68",
                                         borderRadius: "14px", padding: "2px 12px", fontSize: "12px",
                                         fontWeight: on ? 600 : 400, lineHeight: "18px" }}>
                            {m.label}
                            <span style={{ marginLeft: "6px", fontSize: "11px", opacity: on ? 0.9 : 0.65 }}>
                                {m.allocated === null ? m.trips : (m.allocated + "/" + m.trips)}
                            </span>
                        </button>
                    );
                })}
                {this.state.modeLoading ?
                    <span style={{ fontSize: "11px", color: "#777" }}>loading...</span> : ""}
            </div>
        );
    }

    // ONE table per transporter-zone (client, 2026-10-14): the share of business it is owed beside
    // the trucks it actually has today. Kept together because a trip waits only when one of the two
    // sides is missing - share without trucks, or trucks without share - and that is invisible when
    // the two are read as separate tables.
    renderSobAndTrucks() {
        var sob = this.sobRows();
        var trucks = this.availabilityRows();
        var byKey = {};
        var keyOf = function (zone, code) { return zoneKey(zone) + "|" + String(code || ""); };

        sob.forEach(function (s) {
            byKey[keyOf(s.zone, s.code)] = {
                zone: s.zone, name: s.name, code: s.code, approved: s.approved, actual: s.actual,
                deficit: s.deficit, hasShare: true,
                available: 0, used: 0, free: 0, types: []
            };
        });
        trucks.forEach(function (t) {
            var k = keyOf(t.zone, t.code);
            var row = byKey[k];
            if (!row) {
                // Trucks punched for a transporter with no approved share in that zone - SOB can
                // never use them, which is worth seeing rather than hiding.
                row = byKey[k] = { zone: t.zone || "(no zone)", name: t.transporter, code: t.code,
                                   approved: 0, actual: 0, deficit: 0, hasShare: false,
                                   available: 0, used: 0, free: 0, types: [] };
            }
            row.available += t.available;
            row.used += t.used;
            row.free += t.free;
            if (t.available > 0) { row.types.push(t.vehicle_type + " " + t.available); }
            if (!row.typeKeys) { row.typeKeys = {}; }
            row.typeKeys[normalisedVehicleType(t.vehicle_type)] = true;
            if (!row.name) { row.name = t.transporter; }
        });

        // Which (zone, vehicle size) combinations still have a trip WAITING for a truck. A free
        // truck that matches none of them is not a missed allocation - SOB allocates a truck only
        // to a trip of the same zone and the same vehicle type, so there is nothing for it to take.
        // Saying so on the row answers the question the number otherwise provokes ("why was this
        // one not used?"), which on DEV GUR 20261016095512-30273a434ce8 was asked of a free NORTH
        // 20 FT truck at a point when every NORTH 20 FT trip already had a transporter.
        var waitingSet = {};
        var activeKey = this.state.activeMode;
        ((this.state.status || {}).trips || []).forEach(function (t) {
            if (t.transporter) { return; }
            if (modeOfTrip(t) !== activeKey) { return; }
            waitingSet[zoneKey(t.zone) + "|" + normalisedVehicleType(t.vehicle_type)] = true;
        });

        var rows = Object.keys(byKey).map(function (k) { return byKey[k]; });
        rows.forEach(function (r) {
            var zk = zoneKey(r.zone);
            r.needed = Object.keys(r.typeKeys || {}).some(function (tk) { return !!waitingSet[zk + "|" + tk]; });
        });
        rows.sort(function (a, b) {
            return String(a.zone).localeCompare(String(b.zone)) || (b.deficit - a.deficit)
                || String(a.name).localeCompare(String(b.name));
        });

        // One hue per column group so the eye can tell the two halves apart at a glance. The pair
        // is validated for colour-vision deficiency (deep teal vs violet, dE 17.7) and is kept
        // clear of the DATA colours already in use - orange means owed/waiting, blue means free.
        var SHARE = "#7e22ce";      // violet  - share of business
        var TRUCK = "#0f766e";      // teal    - truck availability
        // Both header rows are PINNED while the body scrolls: the group row at the top, the column
        // row directly beneath it (offset by the group row's own height). Each cell carries its own
        // opaque background - a sticky <tr> alone does not paint, so rows would show through.
        var GRP_H = 26;
        var th = { padding: "5px 8px", background: "#eef2f7", fontSize: "11.5px", textAlign: "left",
                   whiteSpace: "nowrap", color: "#334e68", position: "sticky", top: GRP_H + "px", zIndex: 2 };
        var thShare = Object.assign({}, th, { background: "#f5edfd", color: "#5b1a9c" });
        var thTruck = Object.assign({}, th, { background: "#e9f6f4", color: "#0b5a54" });
        var grp = { padding: "5px 8px", fontSize: "11.5px", textAlign: "center", fontWeight: 700,
                    whiteSpace: "nowrap", color: "#fff", borderLeft: "2px solid #fff",
                    position: "sticky", top: 0, zIndex: 3, height: GRP_H + "px" };
        var td = { padding: "4px 8px", fontSize: "12px", borderTop: "1px solid #eee", whiteSpace: "nowrap" };
        var num = Object.assign({}, td, { textAlign: "right" });
        var tdShare = Object.assign({}, num, { background: "#faf5ff" });
        var tdTruck = Object.assign({}, num, { background: "#f0fdfa" });
        var mode = this.activeModeDef();
        var feed = !!mode.trucks;
        var actionable = feed ? rows.filter(function (r) { return r.deficit > 0.05 && r.free > 0; }).length : 0;
        // Shown for a PAST plan too (client, 2026-10-14) - the share and truck picture is useful to
        // look at even when SOB can no longer run on it, so the wording says "today" plainly and
        // never implies a re-run. The Run button stays hidden for those plans (see the footer).
        var readOnly = !!(this.state.status && this.state.status.plan_is_today === false);
        return (
            <div>
                <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    <b>Share of business and trucks{readOnly ? " - Today" : ""}</b>
                    <button type="button" className="btn btn-sm btn-secondary" style={{ padding: "1px 8px", fontSize: "11px" }}
                            onClick={() => this.load()}>Refresh</button>
                    <span style={{ fontSize: "11px", color: "#777" }}>
                        {readOnly
                            ? "this plan is from an earlier day - shown for reference, SOB cannot run on it"
                            : (mode.air
                                ? "air courier is planned on the VIP path - SOB does not allocate it, shown for reference"
                                : !feed
                                ? mode.label + " vehicles are assigned from the share itself - no truck feed exists for them"
                                : (actionable > 0
                                    ? actionable + " transporter(s) owed share with a free truck - a re-run would use them"
                                    : "no transporter is both owed share and holding a free truck"))}
                    </span>
                </div>
                {this.renderModeTabs()}
                <div style={{ fontSize: "11px", color: "#777" }}>
                    {mode.label} approved vs actual share today
                    {feed ? ", beside the live truck feed. Largest deficit is served first; zone-less trucks are never used."
                          : ". Largest deficit is served first."}
                    {readOnly ? " Figures are today's, not this plan's day - its own snapshot is in the audit log." : ""}
                </div>
                {this._availHiddenTrucks > 0 ?
                    <div style={{ fontSize: "11px", color: "#8a6d1f", background: "#fffbe6",
                                  border: "1px solid #f2e3a8", borderRadius: "4px",
                                  padding: "3px 8px", marginTop: "4px" }}>
                        {this._availHiddenTrucks} truck(s) of other types ({this._availHiddenTypes.join(", ")})
                        are punched today but not counted above - this plan has no {mode.label.toLowerCase()} trip
                        of those types, so SOB cannot allocate them to it.
                    </div> : ""}
                {/* Trucks of the shared plant that belong to ANOTHER warehouse (Node with_ignored):
                    never fetched by the engine, so never allocatable here. Named, not just hidden. */}
                {feed ? <div style={{ marginTop: "4px" }}>
                            {renderIgnoredTrucksNotice(this.state.ignoredTrucks,
                                                       (this.state.status || {}).loading_port_code)}
                        </div> : ""}
                <div style={{ maxHeight: "300px", overflowY: "auto", marginTop: "4px" }}>
                    {/* separate (not collapse): a collapsed table drops the borders of a sticky
                        header as it scrolls, leaving the band edges ragged. */}
                    <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                        <thead>
                            <tr>
                                <th style={Object.assign({}, grp, { background: "#64748b" })} colSpan={3}></th>
                                <th style={Object.assign({}, grp, { background: SHARE })} colSpan={3}>
                                    {mode.label} share of business (Today)</th>
                                <th style={Object.assign({}, grp, { background: feed ? TRUCK : "#94a3b8" })} colSpan={4}>
                                    {feed ? "Truck availability (Today)" : "Vehicle availability - no live feed"}</th>
                            </tr>
                            <tr>
                                <th style={Object.assign({}, th, { width: "120px" })}>Zone</th>
                                <th style={th}>Transporter</th>
                                <th style={Object.assign({}, th, { width: "80px" })}>Code</th>
                                <th style={Object.assign({}, thShare, { width: "92px", textAlign: "right", borderLeft: "2px solid #fff" })}>Approved %</th>
                                <th style={Object.assign({}, thShare, { width: "84px", textAlign: "right" })}>Actual %</th>
                                <th style={Object.assign({}, thShare, { width: "80px", textAlign: "right" })}>Deficit</th>
                                <th style={Object.assign({}, thTruck, { width: "80px", textAlign: "right", borderLeft: "2px solid #fff" })}>Available</th>
                                <th style={Object.assign({}, thTruck, { width: "86px", textAlign: "right" })}>Used Today</th>
                                <th style={Object.assign({}, thTruck, { width: "62px", textAlign: "right" })}>Free</th>
                                <th style={Object.assign({}, thTruck, { width: "150px" })}>By vehicle type</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ?
                                <tr><td style={td} colSpan={10}>
                                    {feed ? "No approved share and no trucks for today"
                                          : "No approved " + mode.label + " share for today"}
                                </td></tr> :
                                rows.map(function (r, i) {
                                    var dcol = r.deficit > 0.05 ? "#e65100" : (r.deficit < -0.05 ? "#64748b" : "#334e68");
                                    return (
                                        <tr key={i} style={r.zone === "(no zone)" ? { color: "#999" } : {}}>
                                            <td style={td}>{r.zone}</td>
                                            <td style={Object.assign({}, td, { whiteSpace: "normal" })}>{r.name || "-"}</td>
                                            <td style={td}>{r.code}</td>
                                            {r.hasShare ?
                                                <React.Fragment>
                                                    <td style={Object.assign({}, tdShare, { borderLeft: "2px solid #fff" })}>{r.approved.toFixed(2)}</td>
                                                    <td style={tdShare}>{r.actual.toFixed(2)}</td>
                                                    <td style={Object.assign({}, tdShare, { color: dcol, fontWeight: 600 })}>
                                                        {(r.deficit > 0 ? "+" : "") + r.deficit.toFixed(2)}
                                                    </td>
                                                </React.Fragment>
                                                :
                                                <td style={Object.assign({}, tdShare, { textAlign: "left", color: "#b45309",
                                                                                        borderLeft: "2px solid #fff" })} colSpan={3}>
                                                    no approved share - cannot be allocated
                                                </td>}
                                            {feed ?
                                                <React.Fragment>
                                                    <td style={Object.assign({}, tdTruck, { borderLeft: "2px solid #fff" })}>{r.available}</td>
                                                    <td style={tdTruck}>{r.used}</td>
                                                    <td style={Object.assign({}, tdTruck, { fontWeight: 600, color: r.free > 0 ? "#1565c0" : "#334e68" })}>{r.free}</td>
                                                    <td style={Object.assign({}, tdTruck, { textAlign: "left", whiteSpace: "normal",
                                                                                            color: "#64748b", fontSize: "11px" })}>
                                                        {r.types.join(", ") || "-"}
                                                        {r.free > 0 && !r.needed ?
                                                            <span style={{ color: "#8a6d1f" }}> &middot; no waiting trip for this zone &amp; type</span>
                                                            : ""}
                                                    </td>
                                                </React.Fragment>
                                                :
                                                // No feed for this mode - blank rather than a 0 that
                                                // would read as "no vehicles available".
                                                <td style={Object.assign({}, tdTruck, { textAlign: "left", color: "#94a3b8",
                                                                                        fontSize: "11px", background: "#f8fafc",
                                                                                        borderLeft: "2px solid #fff" })} colSpan={4}>-</td>}
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // Approved share valid TODAY with the actual share and the resulting deficit - the three
    // numbers SOB ranks transporters on.
    sobRows() {
        var totals = {};
        (this.state.zoneTotals || []).forEach(function (z) {
            totals[zoneKey(z.zone)] = parseFloat(z.totalLoad) || 0;
        });
        var loads = {};
        (this.state.actualLoad || []).forEach(function (a) {
            loads[zoneKey(a.zone) + "|" + String(a.transporter_code || "")] = parseFloat(a.load) || 0;
        });
        var rows = (this.state.approved || []).map(function (s) {
            var zk = zoneKey(s.zone);
            var key = zk + "|" + String(s.transporter_code || "");
            var total = totals[zk] || 0;
            var load = loads[key] || 0;
            var approvedPct = parseFloat(s.sob) || 0;
            var actualPct = total > 0 ? (load * 100) / total : 0;
            return {
                zone: String(s.zone || ""), code: String(s.transporter_code || ""),
                name: String(s.transporter_name || ""), approved: approvedPct,
                actual: actualPct, deficit: approvedPct - actualPct, load: load
            };
        }).filter(function (r) { return r.approved > 0; });
        rows.sort(function (a, b) {
            return a.zone.localeCompare(b.zone) || (b.deficit - a.deficit);
        });
        return rows;
    }

    // SOB audit log for THIS plan - one row per run, with the vehicle availability behind each.
    renderAuditLog() {
        var runs = this.state.runs;
        var th = { padding: "5px 8px", background: "#f5f5f5", fontSize: "11.5px", position: "sticky", top: 0,
                   textAlign: "left", whiteSpace: "nowrap", color: "#334e68" };
        var td = { padding: "4px 8px", fontSize: "12px", borderTop: "1px solid #eee", whiteSpace: "nowrap" };
        var self = this;
        return (
            <div>
                <b>SOB audit log</b>
                <span style={{ fontSize: "12px", color: "#555", marginLeft: "8px" }}>
                    {runs.length} run(s) recorded for this plan
                </span>
                {runs.length === 0 ?
                    <div style={{ fontSize: "12px", color: "#777", marginTop: "4px" }}>
                        No run log for this plan - runs made before run logging are not recorded.
                    </div>
                    :
                    <div style={{ maxHeight: "220px", overflowY: "auto", marginTop: "4px" }}>
                        {/* separate, so the sticky header keeps its edges while the runs scroll */}
                        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                            <thead><tr>
                                <th style={Object.assign({}, th, { width: "52px" })}>Run</th>
                                <th style={Object.assign({}, th, { width: "132px" })}>Started</th>
                                <th style={Object.assign({}, th, { width: "58px" })}>Took</th>
                                <th style={Object.assign({}, th, { width: "170px" })}>Requested by</th>
                                <th style={Object.assign({}, th, { width: "130px" })}>Status</th>
                                <th style={Object.assign({}, th, { width: "150px" })}>Trips alloc / kept / wait</th>
                                <th style={Object.assign({}, th, { width: "160px" })}>Modes run</th>
                                <th style={Object.assign({}, th, { width: "80px" })}>Trucks</th>
                                <th style={Object.assign({}, th, { width: "90px" })}>Used earlier</th>
                                <th style={th}>Source</th>
                                <th style={Object.assign({}, th, { width: "70px", textAlign: "center" })}>Vehicles</th>
                            </tr></thead>
                            <tbody>
                                {runs.map(function (r, i) {
                                    return (
                                        <tr key={i}>
                                            <td style={td}>{r.run_no}</td>
                                            <td style={td}>{fmtRunTime(r.started_at)}</td>
                                            <td style={td}>{duration(r.started_at, r.finished_at)}</td>
                                            <td style={Object.assign({}, td, { overflow: "hidden", textOverflow: "ellipsis", maxWidth: "170px" })}
                                                title={r.requested_by || ""}>{r.requested_by || "-"}</td>
                                            <td style={td}><span dangerouslySetInnerHTML={{ __html: sobStatusBadge(r.status, true) }} /></td>
                                            <td style={td}>{(r.new_trips || 0)} / {(r.kept_trips || 0)} / {(r.pending_trips || 0)}</td>
                                            <td style={Object.assign({}, td, { whiteSpace: "normal" })}
                                                title={stageText(r, true)}>{stageText(r, false)}</td>
                                            <td style={td}>{r.trucks_seen}</td>
                                            <td style={td}>{r.trucks_used_today}</td>
                                            <td style={Object.assign({}, td, { whiteSpace: "normal" })}>{sourceText(r.availability_source)}</td>
                                            <td style={Object.assign({}, td, { textAlign: "center" })}>
                                                <i className="icofont icofont-eye-alt" title="Vehicle availability at this run"
                                                   style={{ cursor: "pointer", color: "#1976d2", fontSize: "16px" }}
                                                   onClick={() => self.openRunDetail(r)}></i>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>}
            </div>
        );
    }

    // Allocation summary. The trip-by-trip list is NOT repeated here (client, 2026-10-14): the plan
    // view already lists every trip with its transporter, vehicle number, tentative arrival and SOB
    // run no, and the download carries the same columns. This panel answers "how far did SOB get".
    // Read from the plan output, so it also covers plans SOB'd before vehicle assignment existed.
    renderSummary() {
        var st = this.state.status || {};
        var trips = st.trips || [];
        if (trips.length === 0) { return null; }
        var allocated = trips.filter(function (t) { return t.transporter; }).length;
        var waiting = trips.length - allocated;
        // A VEHICLE NUMBER means a real registration plate off the portal feed. It used to be
        // counted as `vehicle_number || vehicle_source`, which also swept in every MANUAL
        // allocation - and a manual truck is a COUNT, it has no number by construction
        // (sob_incremental writes veh_no = '' for one). DEV GUR 20261016095512-30273a434ce8 showed
        // "With vehicle number 9" when exactly ONE trip carried a plate (HR55BC7356) and the other
        // 8 were manual. The tile is the thing a user takes to the gate, so it has to mean what it
        // says; the manual allocations are real and still reported, as their own figure.
        var withVeh = trips.filter(function (t) { return t.vehicle_number; }).length;
        var manualAlloc = trips.filter(function (t) {
            return !t.vehicle_number && t.vehicle_source === "Manual";
        }).length;
        var hubTrips = trips.filter(function (t) { return t.hub_trip; }).length;
        var cmtWaiting = trips.filter(function (t) { return !t.transporter; })
            .reduce(function (a, t) { return a + (parseFloat(t.cmt) || 0); }, 0);
        var pct = trips.length > 0 ? Math.round((allocated / trips.length) * 100) : 0;

        // zone -> allocated / waiting, the only breakdown that drives a decision (where to chase
        // trucks). It follows the SELECTED MODE (client, 2026-10-14): a hub zone and a regular zone
        // are served from different shares and different trucks, so mixing them in one chart asks
        // the reader to separate them by eye. The tiles above stay whole-plan.
        var modes = this.planModes();
        var active = this.state.activeMode;
        var chartTrips = modes.length > 1
            ? trips.filter(function (t) { return modeOfTrip(t) === active; })
            : trips;
        var modeLabel = modes.length > 1 ? modeDef(active).label : "";
        var zones = {};
        chartTrips.forEach(function (t) {
            var z = String(t.zone || "(no zone)");
            if (!zones[z]) { zones[z] = { zone: z, allocated: 0, waiting: 0 }; }
            if (t.transporter) { zones[z].allocated += 1; } else { zones[z].waiting += 1; }
        });
        var zoneRows = Object.keys(zones).map(function (k) { return zones[k]; })
            .sort(function (a, b) { return (b.waiting - a.waiting) || (b.allocated - a.allocated); });
        var widest = zoneRows.reduce(function (m, z) { return Math.max(m, z.allocated + z.waiting); }, 0) || 1;
        // Few zones -> fatter bars, so a 2-zone warehouse does not draw two thin slivers in a wide
        // panel; many zones -> the narrow bar keeps the group scrollable at a readable width.
        var barW = zoneRows.length <= 3 ? 86 : (zoneRows.length <= 6 ? 66 : 52);
        var barGap = zoneRows.length <= 3 ? 28 : (zoneRows.length <= 6 ? 20 : 14);

        var tile = function (label, value, sub, color) {
            return (
                <div style={{ flex: "1 1 0", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "8px 10px" }}>
                    <div style={{ fontSize: "20px", fontWeight: 600, color: color || "#1e293b", lineHeight: "24px" }}>{value}</div>
                    <div style={{ fontSize: "11.5px", color: "#334e68" }}>{label}</div>
                    {sub ? <div style={{ fontSize: "10.5px", color: "#64748b" }}>{sub}</div> : ""}
                </div>
            );
        };
        // Two states, two hues - validated for colour-vision deficiency, and every segment is
        // labelled as well, so the split never depends on colour alone.
        var ALLOC = "#1565c0";
        var WAIT = "#e65100";
        return (
            <div>
                <b>Allocation summary</b>
                <div className="d-flex" style={{ gap: "8px", marginTop: "6px" }}>
                    {tile("Truck trips", trips.length, hubTrips ? (hubTrips + " hub") : "courier / hold not counted")}
                    {tile("With transporter", allocated, pct + "% of truck trips", ALLOC)}
                    {tile("Waiting for a truck", waiting, waiting > 0 ? (cmtWaiting.toFixed(2) + " CMT") : "none", waiting > 0 ? WAIT : "#1e293b")}
                    {tile("With vehicle number", withVeh,
                          manualAlloc > 0 ? (manualAlloc + " more allocated manually (no number)")
                                          : "from the live truck feed")}
                </div>
                {this.renderModeBreakdown()}
                <div className="d-flex align-items-center" style={{ gap: "12px", margin: "10px 0 4px 0" }}>
                    <span style={{ fontSize: "12px", color: "#334e68", fontWeight: 600 }}>
                        By zone{modeLabel ? " - " + modeLabel : ""}</span>
                    <span style={{ fontSize: "11px", color: "#64748b" }}>
                        <span style={{ display: "inline-block", width: "10px", height: "10px", borderRadius: "2px",
                                       background: ALLOC, marginRight: "4px" }}></span>With transporter
                        <span style={{ display: "inline-block", width: "10px", height: "10px", borderRadius: "2px",
                                       background: WAIT, margin: "0 4px 0 10px" }}></span>Waiting
                    </span>
                </div>
                {/* Vertical stacked columns, drawn straight on the panel (client, 2026-10-14) -
                    no card fill or border, just a baseline rule under the bars, so the chart does
                    not read as a separate box. The plot keeps the SAME HEIGHT whatever the mode
                    shows, so switching tabs never makes the sections below jump. The bar group is
                    CENTRED (an inner fit-content box with auto margins) so two zones do not sit
                    stranded against the left edge of a wide panel, and bars widen when there are
                    few of them. With many zones the group scrolls sideways at its fixed width,
                    rather than every bar being squeezed thinner. */}
                <div style={{ overflowX: "auto", borderBottom: "1px solid #e2e8f0",
                              height: (PLOT_H + 52) + "px", padding: "6px 2px 0 2px" }}>
                <div style={{ display: "flex", alignItems: "flex-end", gap: barGap + "px",
                              width: "fit-content", minWidth: "100%", justifyContent: "center",
                              margin: "0 auto" }}>
                    {zoneRows.length === 0 ?
                        <div style={{ fontSize: "11.5px", color: "#94a3b8", alignSelf: "center",
                                      height: PLOT_H + "px", display: "flex", alignItems: "center" }}>
                            No SOB trips in {modeLabel || "this plan"}
                        </div> : ""}
                    {zoneRows.map(function (z, i) {
                        var total = z.allocated + z.waiting;
                        var ah = Math.round((z.allocated / widest) * PLOT_H);
                        var wh = Math.round((z.waiting / widest) * PLOT_H);
                        // a non-zero count always gets a visible sliver, never a 0px bar
                        if (z.allocated > 0 && ah < 4) { ah = 4; }
                        if (z.waiting > 0 && wh < 4) { wh = 4; }
                        // The zone label is trimmed to the bar width, so the WHOLE column carries
                        // the full name - hovering anywhere over it, not only over the label,
                        // reads out the zone and its split.
                        var tip = z.zone + ": " + z.allocated + " with transporter, " + z.waiting + " waiting";
                        return (
                            <div key={i} title={tip}
                                 style={{ width: barW + "px", flex: "0 0 " + barW + "px", textAlign: "center" }}>
                                <div style={{ fontSize: "11px", color: "#334e68", fontWeight: 600, marginBottom: "2px" }}>
                                    {z.allocated}/{total}
                                </div>
                                <div style={{ height: PLOT_H + "px", display: "flex", flexDirection: "column",
                                              justifyContent: "flex-end", gap: "2px" }}>
                                    {z.waiting > 0 ?
                                        <div title={z.waiting + " waiting"}
                                             style={{ height: wh + "px", background: WAIT, borderRadius: "4px 4px 0 0",
                                                      display: "flex", alignItems: "center", justifyContent: "center",
                                                      color: "#fff", fontSize: "10px", fontWeight: 600 }}>
                                            {wh >= 14 ? z.waiting : ""}
                                        </div> : ""}
                                    {z.allocated > 0 ?
                                        <div title={z.allocated + " with transporter"}
                                             style={{ height: ah + "px", background: ALLOC,
                                                      borderRadius: z.waiting > 0 ? "0" : "4px 4px 0 0",
                                                      display: "flex", alignItems: "center", justifyContent: "center",
                                                      color: "#fff", fontSize: "10px", fontWeight: 600 }}>
                                            {ah >= 14 ? z.allocated : ""}
                                        </div> : ""}
                                </div>
                                <div style={{ fontSize: "10.5px", color: "#64748b", marginTop: "4px", overflow: "hidden",
                                              textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "default" }}
                                     title={tip}>{z.zone}</div>
                            </div>
                        );
                    })}
                </div>
                </div>
                <div style={{ fontSize: "11px", color: "#777", marginTop: "4px" }}>
                    Trip-by-trip detail is on the plan view and in the downloaded plan.
                </div>
            </div>
        );
    }

    // How the plan's trips split across the modes, since each is allocated against its own share.
    // Shown only when the plan has more than one - a single-mode plan would just repeat the tiles.
    // Clicking a mode selects its tab in the table below, so the two always agree.
    renderModeBreakdown() {
        var modes = this.planModes();
        if (modes.length < 2) { return null; }
        var self = this;
        return (
            <div className="d-flex align-items-center" style={{ gap: "8px", flexWrap: "wrap", marginTop: "6px" }}>
                <span style={{ fontSize: "12px", color: "#334e68", fontWeight: 600 }}>By mode</span>
                {modes.map(function (m) {
                    return (
                        <span key={m.key} onClick={function () { self.loadMode(m.key); }}
                              title={"Show " + m.label + " share of business below"}
                              style={{ cursor: "pointer", border: "1px solid #e2e8f0", borderRadius: "12px",
                                       padding: "1px 10px", fontSize: "11.5px", color: "#334e68",
                                       background: m.key === self.state.activeMode ? "#eef4fb" : "#fff" }}>
                            {m.label} {m.allocated === null ? <b>{m.trips}</b>
                                                            : <React.Fragment><b>{m.allocated}</b>/{m.trips}</React.Fragment>}
                            {m.waiting > 0 ?
                                <span style={{ color: "#e65100", marginLeft: "5px" }}>{m.waiting} waiting</span> : ""}
                        </span>
                    );
                })}
            </div>
        );
    }

    // Vehicle availability as at one run - Summary / Trucks, the two views the Truck Availability
    // screen has. Both are built from the same lm_sob_run_trucks rows.
    renderRunDetail() {
        var run = this.state.detailRun;
        if (!run) { return null; }
        var summaryCols = [
            { headerName: "Stage", field: "planning_type", width: 100 },
            { headerName: "Zone", field: "zone", width: 120 },
            { headerName: "Transporter", field: "transporter_name", width: 200 },
            { headerName: "Code", field: "transporter_code", width: 90 },
            { headerName: "Vehicle Type", field: "vehicle_type", width: 130 },
            { headerName: "Available", field: "available", width: 100 },
            { headerName: "Allocated", field: "allocated", width: 100,
              cellStyle: { color: "#2e7d32", fontWeight: 600 } },
            { headerName: "Used earlier", field: "used_earlier", width: 110 },
            { headerName: "No zone", field: "no_zone", width: 90 },
            { headerName: "Free", field: "free", width: 90, cellStyle: { color: "#1565c0", fontWeight: 600 } }
        ];
        var truckCols = [
            { headerName: "Stage", field: "planning_type", width: 100 },
            { headerName: "Zone", field: "zone", width: 120, valueGetter: (p) => p.data.zone || "(no zone)" },
            { headerName: "Transporter", field: "transporter_name", width: 200 },
            { headerName: "Code", field: "transporter_code", width: 90 },
            { headerName: "Vehicle Type", field: "vehicle_type", width: 130 },
            { headerName: "Vehicle Number", field: "vehicle_number", width: 140 },
            { headerName: "Tentative Arrival", field: "tentative_arrival", width: 140 },
            { headerName: "Source", field: "source", width: 90 },
            { headerName: "Status", field: "status", width: 160,
              cellStyle: (p) => ({ color: p.value === "Allocated" ? "#2e7d32" : (p.value === "Free" ? "#1565c0" : "#757575"), fontWeight: 600 }) },
            { headerName: "Trip", field: "trip_no", width: 80 }
        ];
        var summaryRows = this.detailSummaryRows();
        var tabBtn = (key, label) => (
            <button type="button" key={key}
                    className={"pill-btn " + (this.state.detailTab === key ? "pill-btn-info" : "pill-btn-info pill-btn-soft")}
                    onClick={() => this.setState({ detailTab: key })}>{label}</button>
        );
        var log = this.state.detailLog || {};
        return (
            <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.45)",
                          zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center" }}
                 onClick={() => this.setState({ detailRun: null })}>
                <div style={{ background: "#fff", borderRadius: "8px", width: "min(1100px, 94vw)", height: "min(640px, 88vh)",
                              display: "flex", flexDirection: "column", padding: "14px 16px" }}
                     onClick={(e) => e.stopPropagation()}>
                    <div className="d-flex align-items-center" style={{ gap: "10px", flexWrap: "wrap" }}>
                        <b style={{ fontSize: "14px" }}>Vehicle availability at SOB Run No {run.run_no}</b>
                        <span dangerouslySetInnerHTML={{ __html: sobStatusBadge(run.status, true) }} />
                        <span style={{ fontSize: "12px", color: "#555" }}>
                            {fmtDate(run.started_at)} | by {run.requested_by} | source: {sourceText(run.availability_source)}
                        </span>
                        <span style={{ marginLeft: "auto", cursor: "pointer", color: "#607d8b" }}
                              onClick={() => this.setState({ detailRun: null })}>✕</span>
                    </div>
                    <div style={{ fontSize: "12px", color: "#555", margin: "6px 0" }}>{log.remarks || run.remarks || ""}</div>
                    <div className="d-flex align-items-center" style={{ gap: "8px", marginBottom: "6px" }}>
                        {tabBtn("summary", "Summary (" + summaryRows.length + ")")}
                        {tabBtn("trucks", "Trucks (" + this.state.detailTrucks.length + ")")}
                    </div>
                    {log.error ?
                        <pre style={{ maxHeight: "80px", overflow: "auto", fontSize: "11px", background: "#fff3f3",
                                      color: "#b71c1c", padding: "6px", margin: "0 0 6px 0" }}>{log.error}</pre>
                        : ""}
                    <div className="ag-theme-balham" style={{ flex: 1, minHeight: 0 }}>
                        {this.state.detailLoading ?
                            <div style={{ fontSize: "12px", color: "#777" }}>Loading vehicles...</div>
                            : this.state.detailTab === "summary" ?
                            <AgGridReact modules={AllModules} columnDefs={summaryCols} rowData={summaryRows}
                                         defaultColDef={GRID_DEFAULTS} rowHeight={34} headerHeight={40}
                                         overlayNoRowsTemplate={"No vehicles recorded for this run (dedicated / courier runs use no real trucks)"} />
                            :
                            <AgGridReact ref={this.trucksGrid} modules={AllModules} columnDefs={truckCols}
                                         rowData={this.state.detailTrucks}
                                         defaultColDef={GRID_DEFAULTS} rowHeight={34} headerHeight={40}
                                         overlayNoRowsTemplate={"No vehicles recorded for this run (dedicated / courier runs use no real trucks)"} />}
                    </div>
                </div>
            </div>
        );
    }

    render() {
        var st = this.state.status;
        var canRun = st && st.can_run && !this.state.starting && !this.state.checking
                     && st.sob_status !== "IN_PROGRESS";
        var reason = "";
        if (st && !st.can_run && st.sob_status !== "IN_PROGRESS") { reason = st.block_reason; }
        // The approved-share gate that used to live here is GONE on purpose: it read the plan's
        // own mode only, so a multi-mode run was refused for a share another mode did not need.
        // The preconditions are now judged across every mode of the run, on the click (runSob).
        var label = st && (st.sob_run_no > 0 || st.sob_status === "COMPLETED" || st.sob_status === "PARTIAL")
            ? "Re-run SOB (waiting trips only)" : "Run SOB";
        // The slider slides in only once the first load is done, so it never appears empty.
        var sliderOpen = this.props.open && this.state.ready;
        var loaderCls = (this.props.open && this.state.loading) ? "show-m" : "show-n";
        return (
            <div>
                {/* App standard loader, shown on open and on every refresh. The shared classes are
                    position:absolute, which inside this panel would size to the nearest positioned
                    ancestor instead of the screen - so the placement is pinned here. */}
                <div className={"dataLoadpage " + loaderCls}
                     style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", zIndex: 1150 }}></div>
                <div className={"dataLoadpageimg " + loaderCls}
                     style={{ position: "fixed", top: "40%", left: "45%", zIndex: 1151 }}>
                    <div className="loader-box"><div className="loader-box"><div className="rotate dashed colored"></div></div></div>
                </div>
                {sliderOpen ? <div className="slider-overlay" onClick={this.props.onClose}></div> : ""}
                <div className={"hub-slider d-flex flex-column " + (sliderOpen ? "open" : "")}
                     style={{ width: "min(1180px, 94vw)", right: sliderOpen ? 0 : "-1200px" }}>
                    {this.renderHeader()}
                    <div className="slider-body flex-grow-1" style={{ minHeight: 0, gap: "12px", overflowY: "auto" }}>
                        {/* The run's own remark is not repeated here - the tiles carry the same
                            counts and the audit log carries it per run (client, 2026-10-14). */}
                        {this.state.message ? <div style={{ color: "#c62828", fontSize: "12px" }}>{this.state.message}</div> : ""}
                        {this.renderSummary()}
                        {this.renderSobAndTrucks()}
                        {this.renderAuditLog()}
                    </div>
                    <div className="slider-footer" style={{ alignItems: "center", gap: "10px" }}>
                        {st && st.plan_is_today === false ?
                            // Past plan: SOB cannot run on it - the panel is an audit view only.
                            <span style={{ color: "#607d8b", fontSize: "12px", fontWeight: 400 }}>
                                Read-only - SOB can be run only on the plans created today
                            </span>
                            :
                            <React.Fragment>
                                {reason ? <span style={{ color: "#c62828", fontSize: "12px", fontWeight: 400 }}>{reason}</span> : ""}
                                <button type="button" className="btn btn-sm btn-success" disabled={!canRun}
                                        onClick={() => this.runSob()}>
                                    {this.state.starting ? "Starting..."
                                        : (this.state.checking ? "Checking..." : label)}
                                </button>
                            </React.Fragment>}
                    </div>
                </div>
                {this.renderRunDetail()}
                {/* Run preconditions. SweetAlert paints at z-index 5000+, above this slider (1002)
                    and above the run-detail popup (1200), so neither can swallow the prompt.
                    whiteSpace pre-line keeps the paragraph breaks the messages are built with. */}
                <SweetAlert
                    show={this.state.alert.open}
                    type={this.state.alert.type}
                    title={this.state.alert.title}
                    confirmBtnText="OK"
                    confirmBtnBsStyle="primary"
                    onConfirm={() => this.closeAlert()}
                >
                    <div style={{ whiteSpace: "pre-line", fontSize: "13px", textAlign: "left" }}>
                        {this.state.alert.message}
                    </div>
                </SweetAlert>
                {/* Partial configuration: the configured modes allocate normally, so this is the
                    user's call, not a refusal. Continue runs SOB exactly as before. */}
                <SweetAlert
                    show={this.state.confirm.open}
                    type="warning"
                    title={this.state.confirm.title}
                    showCancel
                    confirmBtnText="Continue"
                    cancelBtnText="Cancel"
                    confirmBtnBsStyle="primary"
                    cancelBtnBsStyle="default"
                    onConfirm={() => this.acceptWarnings()}
                    onCancel={() => this.closeConfirm()}
                >
                    <div style={{ whiteSpace: "pre-line", fontSize: "13px", textAlign: "left" }}>
                        {this.state.confirm.message}
                    </div>
                </SweetAlert>
            </div>
        );
    }

    closeAlert() {
        this.setState({ alert: { open: false, title: "", message: "", type: "warning" } });
    }

    closeConfirm() {
        this.setState({ confirm: { open: false, title: "", message: "" } });
    }

    // Continue: close the prompt FIRST, then start - so the dialog cannot sit over a run that has
    // already been queued, and a second Continue cannot reach startSob twice.
    acceptWarnings() {
        if (this.state.starting) { return; }
        this.closeConfirm();
        this.startSob();
    }
}
